import { Navigate, Route, Routes } from 'react-router'
import { pathURL } from '@/components/constants/urls'
import { LoginCircuitLayout } from './components/ui/templates'
import {
  ContactsPage,
  ContractDetailPage,
  ContractsListPage,
  TenantContractsListPage,
  OwnerContractsListPage,
  CommunicationsListPage,
  CommunicationsPage,
  CreateContractPage,
  CreateIncidentPage,
  CreateOwnerContractPage,
  CreateOwnerPage,
  CreatePropertyPage,
  CreateSupplierPage,
  CreateTenantFormPage,
  DashboardPage,
  EstatePropertyDetailPage,
  EstatePropertyPage,
  ExpensesReportPage,
  IncidentDetailPage,
  IncidentListPage,
  LoginPage,
  OtpValidationPage,
  OwnerListPage,
  ReportsPage,
  RentChargesReportPage,
  ContractsRoadmapReportPage,
  ResetPasswordPage,
  SignUpPage,
  SupplierListPage,
  TenantListPage,
  UserProfilePage,
  DepositReportPage,
  ManagerRatingsReportPage,
  AlertNoticesReportPage,
} from '@/components/ui/pages'
import { ForgotPasswordPage } from '@/components/ui/pages'
import { ConfirmDialogModal } from './components/ui/templates/ConfirmDialogModal'
import { ErrorModal } from './components/ui/templates/ErrorModal'
import AppLayout from './components/ui/organism/AppLayout'
import {
  CreatePropertyContextProvider,
  CreateContractContextProvider,
  PropertyDetailsContextProvider,
  IncidentDetailContextProvider,
  ContractDetailsContextProvider,
  CreateIncidentContextProvider,
  CreateCommunicationsContextProvider,
  CreateOwnerContractContextProvider,
  OwnerContractDetailsContextProvider,
} from './contexts'
import { InitialAuthCheck, ProtectedRoute, RootRedirector } from './components/special-routes'
import { ClientFinderPage } from './components/ui/pages/ClientFinderPage'
import { OwnerContractDetailPage } from './components/ui/pages/OwnerContractDetailPage'

const App = () => {
  return (
    <>
      <ConfirmDialogModal />
      <ErrorModal />
      <Routes>
        <Route path={pathURL.LOGIN} element={<LoginCircuitLayout />}>
          <Route index element={<LoginPage />} />
          <Route path={pathURL.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
          <Route path={pathURL.RESET_PASSWORD} element={<ResetPasswordPage />} />
          <Route path={pathURL.SIGN_UP} element={<SignUpPage />} />
          <Route path={pathURL.OTP_VALIDATION} element={<OtpValidationPage />} />
        </Route>
        <Route element={<InitialAuthCheck />}>
          <Route element={<ProtectedRoute />}>
            <Route path={pathURL.USER} element={<AppLayout />}>
              <Route index element={<DashboardPage />} />
              <Route path={pathURL.DETAIL} element={<UserProfilePage />} />
              <Route path={pathURL.CONTACTS} element={<ContactsPage />}>
                <Route
                  index
                  element={
                    <Navigate
                      to={`/${pathURL.USER}/${pathURL.CONTACTS}/${pathURL.TENANT}`}
                      replace
                    />
                  }
                />
                <Route path={pathURL.TENANT} element={<TenantListPage />} />
                <Route path={pathURL.OWNER} element={<OwnerListPage />}/>
                <Route path={pathURL.SUPPLIER} element={<SupplierListPage />}/>
              </Route>
              <Route path={pathURL.TENANT}>
                <Route index element={<TenantListPage />}/>
                <Route path={pathURL.CREATE} element={<CreateTenantFormPage />} />
              </Route>
              <Route path={pathURL.OWNER}>
                <Route index element={<OwnerListPage />} />
                <Route path={pathURL.CREATE} element={<CreateOwnerPage />} />
              </Route>
              <Route path={pathURL.SUPPLIER}>
                <Route index element={<SupplierListPage />} />
                <Route path={pathURL.CREATE} element={<CreateSupplierPage />} />
              </Route>
              <Route path={pathURL.PROPERTY}>
                <Route index element={<EstatePropertyPage />} />
                <Route path={pathURL.CREATE} element={
                  <CreatePropertyContextProvider>
                    <CreatePropertyPage />
                  </CreatePropertyContextProvider>
                } />
                <Route path={pathURL.DETAIL} element={
                  <PropertyDetailsContextProvider>
                    <EstatePropertyDetailPage />
                  </PropertyDetailsContextProvider>
                } />
              </Route>
              <Route path={pathURL.CONTRACT} element={<ContractsListPage />}>
                <Route
                  index
                  element={
                    <Navigate
                      to={`/${pathURL.USER}/${pathURL.CONTRACT}/${pathURL.TENANT}`}
                      replace
                    />
                  }
                />
                <Route path={pathURL.TENANT}>
                  <Route index element={<TenantContractsListPage />} />
                  <Route path={pathURL.CREATE} element={
                    <CreateContractContextProvider>
                      <CreateContractPage />
                    </CreateContractContextProvider>
                  } />
                  <Route path={pathURL.DETAIL} element={
                    <ContractDetailsContextProvider>
                      <ContractDetailPage />
                    </ContractDetailsContextProvider>
                  } />
                </Route>
                <Route path={pathURL.OWNER}>
                  <Route index element={<OwnerContractsListPage />} />
                  <Route path={pathURL.CREATE} element={
                    <CreateOwnerContractContextProvider>
                      <CreateOwnerContractPage />
                    </CreateOwnerContractContextProvider>
                  } />
                  <Route path={pathURL.DETAIL} element={
                    <OwnerContractDetailsContextProvider>
                      <OwnerContractDetailPage />
                    </OwnerContractDetailsContextProvider>
                  } />
                </Route>
              </Route>
              <Route path={pathURL.INCIDENTS}>
                <Route index element={<IncidentListPage />}/>
                <Route path={pathURL.CREATE} element={
                  <CreateIncidentContextProvider>
                    <CreateIncidentPage />
                  </CreateIncidentContextProvider>
                } />
                <Route path={pathURL.DETAIL} element={
                  <IncidentDetailContextProvider>
                    <IncidentDetailPage />
                  </IncidentDetailContextProvider>
                } />
              </Route>
              <Route path={pathURL.COMMUNICATIONS}>
                <Route index element={<CommunicationsListPage />} />
                <Route
                  path={pathURL.CREATE}
                  element={
                    <CreateCommunicationsContextProvider>
                      <CommunicationsPage />
                    </CreateCommunicationsContextProvider>
                  }
                />
              </Route>
              <Route path={pathURL.CLIENT_FINDER} element={
                <ClientFinderPage />
              } />
              <Route path={pathURL.REPORTS}>
                <Route index element={<ReportsPage />} />
                <Route path={pathURL.RENT_CHARGES} element={<RentChargesReportPage />} />
                <Route path={pathURL.EXPENSES} element={<ExpensesReportPage />} />
                <Route path={pathURL.CONTRACTS_ROADMAP} element={<ContractsRoadmapReportPage />} />
                <Route path={pathURL.DEPOSITS} element={<DepositReportPage />} />
                <Route path={pathURL.MANAGER_RATINGS} element={<ManagerRatingsReportPage />} />
                <Route path={pathURL.ALERT_NOTICES} element={<AlertNoticesReportPage />} />
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path='/' element={<RootRedirector />} />
        <Route path='*' element={<Navigate to='/' replace />} />
      </Routes>
    </>
  )
}

export default App
